import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactComponent as CopyIcon } from '../../../../../assets/common-assets/copy.svg';
// import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  generateApiKey,
  regenerateSecretKey,
} from '../../../../../services/apiKey';
import { Root, classes } from './utils';
import { API_KEY, SETTING } from '../../../../../constants/staticContent';
import closeIcon from '../../../../../assets/accountSetting-assets/x-circle.svg';

const KeyNameForm = ({
  setOpenKeyNameForm,
  handleApiResponse,
  selectedApiKey,
  handleDownloadSecretKey,
  selectedApiName,
  handleOpenDialog,
  setSelectedApiKey,
  clientId,
  updateClientName,
}) => {
  const [formData, setFormData] = useState(selectedApiName);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [isCopyAPIKey, setIsCopyAPIKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const hasAnyError = !!error;
  const closeKeyNameForm = () => {
    setOpenKeyNameForm(false);
    setFormData('');
    setError('');
    setSelectedApiKey('');
    setIsEditing(false);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;
    const alphabetRegex = /^[a-zA-Z\s]*$/;
    if (!alphabetRegex.test(value)) {
      setError(`${SETTING.API_SECRET_KEY.INVALID_NAME}`);
    } else if (value?.length > 6) {
      setError(`${SETTING.API_SECRET_KEY.VALID_LENGTH}`);
    } else {
      setError('');
    }
    setFormData(value);
  };

  const handleKeyGeneration = async () => {
    if (!formData.trim()) {
      setError(`${SETTING.API_SECRET_KEY.EMPTY_FIELD}`);
      return;
    }

    if (!hasAnyError) {
      const response = await generateApiKey(formData);
      if (response) {
        setSelectedApiKey(response?.data?.apiKeys);
        await handleDownloadSecretKey(response?.data?.apiKeys?.api_key);
        setFormData('');
        setError('');
        handleApiResponse(response?.data);
      }
    }
  };

  const handleEnter = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  const copyApiKey = async (e, apiKey) => {
    e.stopPropagation();
    navigator.clipboard.writeText(apiKey);
    setIsCopyAPIKey(true);
    setTimeout(() => {
      setIsCopyAPIKey(false);
    }, 1000);
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(clientId);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };
  const RegenerateSecretKey = async () => {
    const result = await regenerateSecretKey({ apiKey: selectedApiKey });
    if (result) {
      handleDownloadSecretKey(selectedApiKey);
    }
  };
  const handleEdit = () => {
    setIsEditing(true);
  };
  const updateClient = async () => {
    if (!formData.trim()) {
      setError(`${SETTING.API_SECRET_KEY.EMPTY_FIELD}`);
      return;
    }
    if (formData === selectedApiName) {
      setError(`${SETTING.API_SECRET_KEY.SAME_VALUE}`);
      return;
    }
    if (!hasAnyError) {
      const payload = {
        apiKey: selectedApiKey,
        clientName: formData,
      };
      await updateClientName(payload);
      setIsEditing(false);
    }
  };
  const handleCancelEditing = () => {
    setFormData(selectedApiName);
    setIsEditing(false);
  };

  return (
    <Root className={classes.root}>
      <Card
        className={
          hasAnyError
            ? classes.errorCardComponent
            : selectedApiKey
              ? classes.selectedCardComponent
              : classes.CardComponent
        }
      >
        <CardHeader
          className={classes.CardHeader}
          title={
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <Typography variant="h4">API Key</Typography>
              </Grid>
              <Grid item xs={2} align="right">
                <Avatar
                  onClick={closeKeyNameForm}
                  src={closeIcon}
                  className={classes.closeAction}
                />
              </Grid>
            </Grid>
          }
        />
        <CardContent className={classes.CardContent}>
          <Box component="form" noValidate>
            <Box className={classes.GridBox}>
              <Grid container justifyContent="space-between">
                <Grid item xs={12}>
                  <Box className={classes.GridItems}>
                    {selectedApiKey && (
                      <>
                        <Typography
                          variant="body1"
                          className={classes.GridItemsTypography}
                        >
                          Client Id
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          autoComplete="off"
                          value={clientId}
                          className={classes.GridItemTextfield}
                          disabled
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                                  <IconButton onClick={handleCopy} edge="end">
                                    {copied ? (
                                      <CheckCircleIcon
                                        sx={{ height: '24px', width: '24px' }}
                                      />
                                    ) : (
                                      <CopyIcon
                                        sx={{ height: '24px', width: '24px' }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    )}
                    <Typography
                      variant="body1"
                      className={classes.GridItemsTypography}
                    >
                      Client Name
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      autoComplete="off"
                      value={selectedApiName || formData}
                      className={classes.GridItemTextfield}
                      onChange={handleOnChange}
                      onKeyDown={(e) => handleEnter(e)}
                      error={!!error}
                      helperText={error}
                      disabled={isEditing ? false : selectedApiKey !== ''}
                    />

                    {selectedApiKey && (
                      <>
                        <Typography
                          variant="body1"
                          className={`${classes.GridItemsTypography}`}
                          mt={1}
                        >
                          API Key
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          autoComplete="off"
                          value={selectedApiKey}
                          className={classes.GridItemTextfield}
                          onChange={handleOnChange}
                          onKeyDown={(e) => handleEnter(e)}
                          // error={!!error}
                          // helperText={error}
                          disabled
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip
                                  title={isCopyAPIKey ? 'Copied!' : 'Copy'}
                                >
                                  <IconButton
                                    onClick={(e) =>
                                      copyApiKey(e, selectedApiKey)
                                    }
                                    edge="end"
                                  >
                                    {isCopyAPIKey ? (
                                      <CheckCircleIcon
                                        sx={{ height: '24px', width: '24px' }}
                                      />
                                    ) : (
                                      <CopyIcon
                                        sx={{ height: '24px', width: '24px' }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {!isEditing && (
                          <Button
                            className={classes.updateSecret}
                            onClick={RegenerateSecretKey}
                          >
                            Regenerate Secret Key
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box className={classes.Box}>
            <Button
              className={
                isEditing
                  ? classes.CancelButton
                  : selectedApiKey !== ''
                    ? classes.removeAPIKeyButton
                    : classes.CancelButton
              }
              variant={
                isEditing
                  ? 'contained'
                  : selectedApiKey !== ''
                    ? 'outlined'
                    : 'contained'
              }
              onClick={(e) => {
                if (isEditing) {
                  handleCancelEditing();
                } else if (selectedApiKey !== '') {
                  handleOpenDialog(e);
                } else {
                  closeKeyNameForm();
                }
              }}
            >
              <Typography variant="body1">
                {isEditing
                  ? 'Cancel'
                  : selectedApiKey !== ''
                    ? 'Delete API Key'
                    : 'Cancel'}
              </Typography>
            </Button>
            {selectedApiKey === '' && (
              <Button
                variant="contained"
                className={classes.UpdateButton}
                onClick={
                  isEditing
                    ? updateClient
                    : selectedApiKey !== ''
                      ? handleEdit
                      : handleKeyGeneration
                }
              >
                <Typography variant="body1">
                  {isEditing
                    ? 'Update'
                    : selectedApiKey !== ''
                      ? API_KEY.DOWNLOAD_BUTTON
                      : API_KEY.GENERATE_KEY_BUTTON}
                </Typography>
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Root>
  );
};

KeyNameForm.defaultProps = {
  selectedApiKey: '',
  selectedApiName: '',
};

KeyNameForm.propTypes = {
  setOpenKeyNameForm: PropTypes.func.isRequired,
  handleApiResponse: PropTypes.func.isRequired,
  selectedApiKey: PropTypes.string,
  handleDownloadSecretKey: PropTypes.func.isRequired,
  selectedApiName: PropTypes.string,
  handleOpenDialog: PropTypes.func.isRequired,
  setSelectedApiKey: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
  updateClientName: PropTypes.func.isRequired,
};

export default KeyNameForm;
